import { ApiClient } from "lib/utils/client/apiClient";

// Types based on backend DTOs
export interface CreateAssemblyDto {
  title: string;
  description?: string;
  condoId: string;
  startTime: string; // ISO 8601
  endTime: string; // ISO 8601
  quorum?: number; // 0-100
}

export interface UpdateAssemblyDto {
  title?: string;
  description?: string;
  startTime?: string; // ISO 8601
  endTime?: string; // ISO 8601
  quorum?: number; // 0-100
}

export interface CreateAgendaItemDto {
  title: string;
  description?: string;
  options: string[]; // Dynamic poll options (minimum 2 required)
  votingStart: string; // ISO 8601
  votingEnd: string; // ISO 8601
}

export interface UpdateAgendaItemDto {
  title?: string;
  description?: string;
  options?: string[]; // Dynamic poll options (minimum 2 required)
  votingStart?: string; // ISO 8601
  votingEnd?: string; // ISO 8601
}

export interface CastVoteDto {
  value: string; // Dynamic vote value - must match one of the poll options
  comment?: string;
}

export interface GetAssembliesDto {
  limit?: number;
  page?: number;
}

/**
 * Create a new assembly (meeting/event)
 * POST /voting/assemblies
 */
export const createAssembly = async (data: CreateAssemblyDto) => {
  return ApiClient('/voting/assemblies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Get all assemblies with pagination
 * GET /voting/assemblies?limit=10&page=1
 */
export const getAllAssemblies = async (params?: GetAssembliesDto) => {
  const queryParams = new URLSearchParams();
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.page) queryParams.append('page', params.page.toString());

  const queryString = queryParams.toString();
  const url = `/voting/assemblies${queryString ? `?${queryString}` : ''}`;

  return ApiClient(url, {
    method: 'GET',
  });
};

/**
 * Get assembly by ID
 * GET /voting/assemblies/:id
 */
export const getAssemblyById = async (id: string) => {
  return ApiClient(`/voting/assemblies/${id}`, {
    method: 'GET',
  });
};

/**
 * Update an assembly
 * PUT /voting/assemblies/:id
 */
export const updateAssembly = async (id: string, data: UpdateAssemblyDto) => {
  return ApiClient(`/voting/assemblies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Create an agenda item (poll) within an assembly
 * POST /voting/assemblies/:assemblyId/agenda-items
 */
export const createAgendaItem = async (assemblyId: string, data: CreateAgendaItemDto) => {
  return ApiClient(`/voting/assemblies/${assemblyId}/agenda-items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Update an agenda item (poll)
 * PUT /voting/agenda-items/:agendaItemId
 */
export const updateAgendaItem = async (agendaItemId: string, data: UpdateAgendaItemDto) => {
  return ApiClient(`/voting/agenda-items/${agendaItemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
};

/**
 * Cast a vote on an agenda item
 * POST /voting/agenda-items/:agendaItemId/vote
 */
export const castVote = async (agendaItemId: string, data: CastVoteDto) => {
  return ApiClient(`/voting/agenda-items/${agendaItemId}/vote`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
};

/**
 * Get my votes (for residents)
 * GET /voting/my-votes
 */
export const getMyVotes = async () => {
  return ApiClient('/voting/my-votes', {
    method: 'GET',
  });
};

/**
 * Get vote results for an agenda item (admin only)
 * GET /voting/agenda-items/:agendaItemId/results
 */
export const getVoteResults = async (agendaItemId: string) => {
  return ApiClient(`/voting/agenda-items/${agendaItemId}/results`, {
    method: 'GET',
  });
};

/**
 * Close an agenda item (poll) - Admin only, can close at any time
 * POST /voting/agenda-items/:agendaItemId/close
 */
export const closeAgendaItem = async (agendaItemId: string) => {
  return ApiClient(`/voting/agenda-items/${agendaItemId}/close`, {
    method: 'POST',
  });
};

/**
 * Generate meeting minutes for an assembly (admin only)
 * GET /voting/assemblies/:assemblyId/minutes
 */
export const generateMinutes = async (assemblyId: string) => {
  return ApiClient(`/voting/assemblies/${assemblyId}/minutes`, {
    method: 'GET',
  });
};

